import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { EntityRelationship } from "common/graph/entityGraphTypes";
import type { AABB3 } from "geom/aabb3Types";
import type { RelationshipType } from "./buildingEntityIndex";
import { EntityIndex } from "./buildingEntityIndex";

export interface BuildingGenerationContextInterface {
  indexLoadBuildingEntityGraph(building: Pick<Building, "entityGraphNodes" | "entityGraphRelationships">): void;
  indexNodeHash(node: DocEntityHandle): string;
  indexRelationships(
    node: DocEntityHandle,
    predicate?: (relationship: EntityRelationship<string, RelationshipType>) => boolean,
  ): EntityRelationship<string, RelationshipType>[];
  indexNodeByHash(hash: string): DocEntityHandle | undefined;
  indexNodes(): DocEntityHandle[];
  indexNodeAABB(node: DocEntityHandle): AABB3 | undefined;
  indexAddNode(node: DocEntityHandle, aabb?: AABB3): DocEntityHandle;
  indexNodeByDocEntityHandle(handle: DocEntityHandle): DocEntityHandle | undefined;
  indexRelate(a: DocEntityHandle, type: RelationshipType, b: DocEntityHandle): void;
  indexSpatialSearch(aabb: AABB3, predicate?: (handle: DocEntityHandle) => boolean): DocEntityHandle[];
  indexBreadthFirstSearch(node: DocEntityHandle, depth?: number): DocEntityHandle[];
}

class BuildingGenerationContextImpl implements BuildingGenerationContextInterface {
  private _index: EntityIndex;
  private _currentStageId: string | undefined = undefined;
  private _handlesByStage: Map<string, DocEntityHandle[]>;

  constructor() {
    this._index = new EntityIndex();
    this._handlesByStage = new Map();
  }

  startStage(stageId: string) {
    if (this._currentStageId) {
      console.warn(`stage ${this._currentStageId} was not finalized before starting stage ${stageId}`);
    }
    if (this._handlesByStage.has(stageId)) {
      const handles = this._handlesByStage.get(stageId) ?? [];
      for (const handle of handles) {
        this._index.deleteNode(handle);
      }
    }
    this._currentStageId = stageId;
    this._handlesByStage.set(stageId, []);
  }

  endStage() {
    this._currentStageId = undefined;
  }

  indexLoadBuildingEntityGraph(building: Pick<Building, "entityGraphNodes" | "entityGraphRelationships">) {
    this._index.loadBuildingEntityGraph(building);
  }

  indexNodeHash(node: DocEntityHandle) {
    return this._index.nodeHash(node);
  }

  indexRelationships(
    node: DocEntityHandle,
    predicate?: (relationship: EntityRelationship<string, RelationshipType>) => boolean,
  ) {
    return this._index.relationships(node, predicate);
  }

  indexNodeByHash(hash: string) {
    return this._index.nodeByHash(hash);
  }

  indexNodes() {
    return this._index.nodes();
  }

  indexNodeAABB(node: DocEntityHandle) {
    return this._index.nodeAABB(node);
  }

  indexAddNode(node: DocEntityHandle, aabb?: AABB3) {
    const handle = this._index.addNode(node, aabb);
    const currentStageId = this._currentStageId;
    if (currentStageId !== undefined) {
      this._handlesByStage.get(currentStageId)?.push(handle);
    }
    return handle;
  }

  indexNodeByDocEntityHandle(handle: DocEntityHandle) {
    return this._index.nodeByDocEntityHandle(handle);
  }

  indexRelate(a: DocEntityHandle, type: RelationshipType, b: DocEntityHandle) {
    return this._index.relate(a, type, b);
  }

  indexSpatialSearch(aabb: AABB3, predicate?: (handle: DocEntityHandle) => boolean) {
    return this._index.spatialSearch(aabb, predicate);
  }

  indexBreadthFirstSearch(node: DocEntityHandle, depth: number = 1) {
    return this._index.breadthFirstSearch(node, depth);
  }
}

export const BuildingGenerationContext = new BuildingGenerationContextImpl();
